import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ScatterChart, Scatter, ZAxis, Cell } from 'recharts';
import { TrendingUp, AlertCircle, Shield, Activity, ArrowRight } from 'lucide-react';
interface CaptivePortfolioOverviewProps {
  onNavigate?: (dashboard: string) => void;
}
// Mock Data
const kpiData = {
  gwpYTD: 45.2,
  gwpFY: 62.5,
  netLossRatioYTD: 58.4,
  netLossRatioUltimate: 61.2,
  claimsFrequency: 8.3,
  largeLossCount: 3,
  largeLossRetention: 100,
  activeFleetCells: 14
};
const lossRatioTrend = [{
  month: 'Jan',
  ratio: 55
}, {
  month: 'Feb',
  ratio: 58
}, {
  month: 'Mar',
  ratio: 54
}, {
  month: 'Apr',
  ratio: 60
}, {
  month: 'May',
  ratio: 59
}, {
  month: 'Jun',
  ratio: 62
}, {
  month: 'Jul',
  ratio: 58
}, {
  month: 'Aug',
  ratio: 56
}, {
  month: 'Sep',
  ratio: 55
}, {
  month: 'Oct',
  ratio: 57
}, {
  month: 'Nov',
  ratio: 58
}, {
  month: 'Dec',
  ratio: 56
}];
const exposureGrowth = [{
  month: 'Jan',
  vehicles: 12500
}, {
  month: 'Feb',
  vehicles: 12800
}, {
  month: 'Mar',
  vehicles: 13200
}, {
  month: 'Apr',
  vehicles: 13500
}, {
  month: 'May',
  vehicles: 14100
}, {
  month: 'Jun',
  vehicles: 14500
}, {
  month: 'Jul',
  vehicles: 14800
}, {
  month: 'Aug',
  vehicles: 15200
}, {
  month: 'Sep',
  vehicles: 15600
}, {
  month: 'Oct',
  vehicles: 16100
}, {
  month: 'Nov',
  vehicles: 16500
}, {
  month: 'Dec',
  vehicles: 17000
}];
const cellHeatmapData = [{
  name: 'Ubuntu Fleet',
  lossRatio: 45,
  premium: 5000,
  status: 'good'
}, {
  name: 'Highveld Logistics',
  lossRatio: 68,
  premium: 3000,
  status: 'warning'
}, {
  name: 'Imbokodo Mobility',
  lossRatio: 38,
  premium: 4500,
  status: 'good'
}, {
  name: 'Baobab Transport',
  lossRatio: 35,
  premium: 8000,
  status: 'good'
}, {
  name: 'Karoo Property',
  lossRatio: 52,
  premium: 4000,
  status: 'good'
}, {
  name: 'Drakensberg Asset',
  lossRatio: 41,
  premium: 6000,
  status: 'good'
}, {
  name: 'Mzansi Business',
  lossRatio: 72,
  premium: 2500,
  status: 'critical'
}, {
  name: 'Umhlanga Retail',
  lossRatio: 32,
  premium: 7000,
  status: 'good'
}];
const COLORS = {
  good: '#22C55E',
  warning: '#F59E0B',
  critical: '#EF4444',
  primary: '#EC4899',
  secondary: '#9333EA'
};
export function CaptivePortfolioOverview({
  onNavigate
}: CaptivePortfolioOverviewProps) {
  return <div className="space-y-6 animate-in" data-id="element-473">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-474">
        <div data-id="element-475">
          <h1 className="text-2xl font-bold text-black" data-id="element-476">Portfolio Overview</h1>
          <p className="text-neutral-500 mt-1" data-id="element-477">
            Morning screen: High-level portfolio health and key indicators
          </p>
        </div>
        <div className="flex gap-2" data-id="element-478">
          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium" data-id="element-479">
            Portfolio Healthy
          </span>
        </div>
      </div>

      {/* Top Row: KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-480">
        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-481">
          <div className="flex justify-between items-start mb-2" data-id="element-482">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-483">
              GWP (YTD / FY)
            </p>
            <TrendingUp size={16} className="text-pink-500" data-id="element-484" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-485">
            <h3 className="text-2xl font-bold text-black" data-id="element-486">
              £{kpiData.gwpYTD}M
            </h3>
            <span className="text-sm text-neutral-400" data-id="element-487">
              / £{kpiData.gwpFY}M
            </span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-488">
          <div className="flex justify-between items-start mb-2" data-id="element-489">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-490">
              Net Loss Ratio
            </p>
            <Activity size={16} className="text-purple-500" data-id="element-491" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-492">
            <h3 className="text-2xl font-bold text-black" data-id="element-493">
              {kpiData.netLossRatioYTD}%
            </h3>
            <span className="text-sm text-neutral-400" data-id="element-494">
              (Ult: {kpiData.netLossRatioUltimate}%)
            </span>
          </div>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-495">
            (Incurred + ALAE) / Net Earned Premium
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-496">
          <div className="flex justify-between items-start mb-2" data-id="element-497">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-498">
              Claims Frequency
            </p>
            <Activity size={16} className="text-pink-500" data-id="element-499" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-500">
            <h3 className="text-2xl font-bold text-black" data-id="element-501">
              {kpiData.claimsFrequency}
            </h3>
            <span className="text-sm text-neutral-400" data-id="element-502">per 100 vehicles</span>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-503">
          <div className="flex justify-between items-start mb-2" data-id="element-504">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-505">
              Large Losses
            </p>
            <AlertCircle size={16} className="text-red-500" data-id="element-506" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-507">
            <h3 className="text-2xl font-bold text-black" data-id="element-508">
              {kpiData.largeLossCount}
            </h3>
            <span className="text-sm text-neutral-400" data-id="element-509">Active</span>
          </div>
          <p className="text-[10px] text-neutral-400 mt-1" data-id="element-510">
            Retention: &gt;£{kpiData.largeLossRetention}k (configurable)
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg border border-neutral-200 shadow-sm" data-id="element-511">
          <div className="flex justify-between items-start mb-2" data-id="element-512">
            <p className="text-xs font-medium text-neutral-500 uppercase" data-id="element-513">
              Active Cells
            </p>
            <Shield size={16} className="text-purple-500" data-id="element-514" />
          </div>
          <div className="flex items-baseline gap-2" data-id="element-515">
            <h3 className="text-2xl font-bold text-black" data-id="element-516">
              {kpiData.activeFleetCells}
            </h3>
            <span className="text-sm text-neutral-400" data-id="element-517">Fleet Programmes</span>
          </div>
        </div>
      </div>

      {/* Middle Section: Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-518">
        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-519">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-520">
            Loss Ratio Trend (12 Months)
          </h3>
          <div className="h-64" data-id="element-521">
            <ResponsiveContainer width="100%" height="100%" data-id="element-522">
              <LineChart data={lossRatioTrend} data-id="element-523">
                <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-524" />
                <XAxis dataKey="month" tick={{
                fontSize: 12
              }} data-id="element-525" />
                <YAxis domain={[0, 100]} tick={{
                fontSize: 12
              }} data-id="element-526" />
                <Tooltip data-id="element-527" />
                <Line type="monotone" dataKey="ratio" stroke={COLORS.primary} strokeWidth={2} dot={false} data-id="element-528" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-529">
          <h3 className="text-lg font-medium text-black mb-4" data-id="element-530">
            Exposure Growth (Vehicles)
          </h3>
          <div className="h-64" data-id="element-531">
            <ResponsiveContainer width="100%" height="100%" data-id="element-532">
              <BarChart data={exposureGrowth} data-id="element-533">
                <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-534" />
                <XAxis dataKey="month" tick={{
                fontSize: 12
              }} data-id="element-535" />
                <YAxis tick={{
                fontSize: 12
              }} data-id="element-536" />
                <Tooltip data-id="element-537" />
                <Bar dataKey="vehicles" fill={COLORS.secondary} radius={[4, 4, 0, 0]} data-id="element-538" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Section: Cell Heatmap */}
      <div className="bg-white p-6 rounded-lg border border-neutral-200 shadow-sm" data-id="element-539">
        <div className="flex justify-between items-center mb-4" data-id="element-540">
          <h3 className="text-lg font-medium text-black" data-id="element-541">
            Cell Performance Heatmap
          </h3>
          <p className="text-sm text-neutral-500" data-id="element-542">
            Size = Premium Volume | Color = Loss Ratio Status
          </p>
        </div>
        <div className="h-80" data-id="element-543">
          <ResponsiveContainer width="100%" height="100%" data-id="element-544">
            <ScatterChart margin={{
            top: 20,
            right: 20,
            bottom: 20,
            left: 20
          }} data-id="element-545">
              <CartesianGrid data-id="element-546" />
              <XAxis type="category" dataKey="name" name="Cell" tick={{
              fontSize: 12
            }} data-id="element-547" />
              <YAxis type="number" dataKey="lossRatio" name="Loss Ratio" unit="%" domain={[0, 100]} data-id="element-548" />
              <ZAxis type="number" dataKey="premium" range={[100, 1000]} name="Premium" data-id="element-549" />
              <Tooltip cursor={{
              strokeDasharray: '3 3'
            }} data-id="element-550" />
              <Scatter name="Cells" data={cellHeatmapData} fill="#8884d8" onClick={() => onNavigate?.('Cell Performance')} cursor="pointer" data-id="element-551">
                {cellHeatmapData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.status === 'good' ? COLORS.good : entry.status === 'warning' ? COLORS.warning : COLORS.critical} data-id="element-552" />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 flex justify-end" data-id="element-553">
          <button onClick={() => onNavigate?.('Cell Performance')} className="text-sm font-medium text-pink-600 hover:text-pink-700 flex items-center gap-1" data-id="element-554">
            Drill down to Cell Performance <ArrowRight size={14} data-id="element-555" />
          </button>
        </div>
      </div>
    </div>;
}